import { GKButton, Grid2, Switch, Typography } from "components";
import { CompanySettingApi } from "interfaces/Settings";
import { Controller, useForm } from "react-hook-form";
import { useUpdateCompanySettingsMutation } from "store/features/CompanySetting";
import { useAppSelector } from "store/store";
import useDocumentTitle from "utils/useDocumentTitle";
import PortfolioMetricsComponent from "./PortfolioMetricsComponent";

type FormFields = {
  tradesPlusCash: boolean;
  includeFeeInDeposit: boolean;
  showTwrr: boolean;
  cashXirrWithDiv: boolean;
};

interface Props {
  data: CompanySettingApi;
  companyId: string;
}

const PortfolioMetrics = ({ data, companyId }: Props) => {
  useDocumentTitle("Portfolio Metrics");

  const [updateCompanySettings, { isLoading }] =
    useUpdateCompanySettingsMutation();

  const isNavEnabled = useAppSelector((state) => state?.user?.userData?.nav);

  const generalOptions = [
    {
      name: "NAV, XIRR and Net Contribution with cash flows",
      fieldName: "tradesPlusCash",
      description:
        "Select this option to calculate portfolio metrics using cash flows only(Deposits and Withdrawals)",
    },
    {
      name: "Include fees in returns calculations(XIRR, NAV, Net Contribution)",
      fieldName: "includeFeeInDeposit",
      description:
        "Select this option to include fee deposits in cash flows for return metrics((XIRR, NAV, Net Contribution). ",
    },
    {
      name: "Include dividend in XIRR calculation",
      fieldName: "cashXirrWithDiv",
      description: "Turn on the toggle to include dividend in XIRR calculation",
    },
  ];

  const form = useForm({
    defaultValues: {
      tradesPlusCash: data?.tradesPlusCash || false,
      includeFeeInDeposit: data?.includeFeeInDeposit || false,
      showTwrr: data?.showTwrr || false,
      cashXirrWithDiv: data?.cashXirrWithDiv || false,
    },
    values: {
      tradesPlusCash: data?.tradesPlusCash || false,
      includeFeeInDeposit: data?.includeFeeInDeposit || false,
      showTwrr: data?.showTwrr || false,
      cashXirrWithDiv: data?.cashXirrWithDiv || false,
    },
  });

  return (
    <form
      onSubmit={form.handleSubmit((values) =>
        updateCompanySettings({
          id: companyId,
          body: values,
        }),
      )}
    >
      <Grid2 container spacing={2}>
        {generalOptions.map((dataItem) => (
          <Grid2 size={12} key={dataItem.fieldName}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Controller
                name={dataItem.fieldName as keyof FormFields}
                control={form.control}
                render={({ field }) => (
                  <Switch checked={field.value} onChange={field.onChange} />
                )}
              />
              <Typography fontSize={16} fontWeight={400}>
                {dataItem.name}
              </Typography>
            </div>
            <Typography ml={"50px"} fontSize={12} color={"var(--neutral-500)"}>
              {dataItem.description}
            </Typography>
          </Grid2>
        ))}

        {isNavEnabled && (
          <Grid2
            size={12}
            sx={{
              mt: 2,
            }}
          >
            <PortfolioMetricsComponent
              control={form.control as any}
              name="showTwrr"
              label="Returns Metrics"
              falseLabel="XIRR"
              trueLabel="TWRR"
              descriptionFalse="XIRR(calculated using cash flows or trades) will be used as returns metrics in the portfolio dashboard, benchmark comparison, and reports."
              descriptionTrue="TWRR(calculated using NAVs) will be used as returns metrics in the portfolio dashboard, benchmark comparison, and reports."
            />
          </Grid2>
        )}
        <Grid2 size={12} display={"flex"} justifyContent={"flex-end"}>
          <GKButton variant="contained" type="submit" loading={isLoading}>
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default PortfolioMetrics;
